// Calendars & tasks account state, shared by every surface that shows it
// (ProfileButton row, More row, the dialog / push screens).
//
// One module-level store, not per-component state: three surfaces mount this
// at once, and each fetching on its own would triple the requests. The same
// single-flight shape as entitlementApi.
//
// Cached under INTEGRATIONS_CACHE_KEY (user-scoped, cleared on sign-out and
// account switch), so the entry row and list render their last-known state
// immediately and reconcile silently (spec §3.2 "Loading, with cache").

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  disconnectIntegration,
  getIntegrationAccounts,
  getIntegrationsEnabled,
  INTEGRATION_ERROR,
  isIntegrationsApiConfigured,
} from '../services/integrationsApi';
import { graphqlErrorCode } from '../services/graphqlClient';
import { loadOwnedFromStorageAsync, saveOwnedToStorageAsync } from '../services/storage';
import { warn } from '../utils/logger';
import type { IntegrationAccount } from '../types';

export const INTEGRATIONS_CACHE_KEY = 'rhemn:integrations:v1';

/**
 * - `idle`: nothing fetched yet, no cache.
 * - `ready`: the last fetch succeeded.
 * - `error`: the last fetch failed; `accounts` is the cache (may be empty).
 * - `unavailable`: the server reports INTEGRATIONS_UNAVAILABLE (ops failure):
 *   rows render inert (spec §3.2).
 */
export type IntegrationsLoadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface IntegrationsSnapshot {
  uid: string | null;
  enabled: boolean;
  accounts: IntegrationAccount[];
  status: IntegrationsLoadStatus;
}

interface CachedIntegrations {
  enabled: boolean;
  accounts: IntegrationAccount[];
}

const EMPTY: IntegrationsSnapshot = { uid: null, enabled: false, accounts: [], status: 'idle' };

let snapshot: IntegrationsSnapshot = EMPTY;
let inFlight: Promise<void> | null = null;
let inFlightUid: string | null = null;
const listeners = new Set<() => void>();

const emit = (next: IntegrationsSnapshot): void => {
  snapshot = next;
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = (): IntegrationsSnapshot => snapshot;

/** Read the store outside React (calendar sources, P5 auto-sync). */
export const getIntegrationsSnapshot = (): IntegrationsSnapshot => snapshot;
/** Subscribe outside React. Returns the unsubscribe function. */
export const subscribeIntegrations = subscribe;

/** Stamped with the owner uid: another account's accounts never seed this one. */
const persist = (s: IntegrationsSnapshot): void => {
  if (!s.uid) return;
  const cached: CachedIntegrations = { enabled: s.enabled, accounts: s.accounts };
  void saveOwnedToStorageAsync(INTEGRATIONS_CACHE_KEY, s.uid, cached);
};

const isCached = (v: unknown): v is CachedIntegrations =>
  typeof v === 'object' && v !== null && Array.isArray((v as CachedIntegrations).accounts);

/** Fetch the flag and the accounts together; either may fail on its own. */
const load = (uid: string): Promise<void> => {
  if (inFlight && inFlightUid === uid) return inFlight;
  // A load for the previous account is still out: let it settle (its result
  // is dropped below), then load this one.
  if (inFlight) return inFlight.then(() => load(uid));
  inFlightUid = uid;
  inFlight = (async () => {
    if (snapshot.uid !== uid) {
      // First load for this account: seed from the cache before the network.
      const cached = await loadOwnedFromStorageAsync(INTEGRATIONS_CACHE_KEY, uid);
      emit(isCached(cached)
        ? { uid, enabled: cached.enabled === true, accounts: cached.accounts, status: 'loading' }
        : { ...EMPTY, uid, status: 'loading' });
    } else {
      emit({ ...snapshot, status: snapshot.status === 'idle' ? 'loading' : snapshot.status });
    }

    const [enabledRes, accountsRes] = await Promise.allSettled([
      getIntegrationsEnabled(),
      getIntegrationAccounts(),
    ]);
    // Signed out or switched account while we were waiting: drop the result.
    if (snapshot.uid !== uid) return;

    const enabled = enabledRes.status === 'fulfilled' ? enabledRes.value : snapshot.enabled;
    if (accountsRes.status === 'fulfilled') {
      const next: IntegrationsSnapshot = { uid, enabled, accounts: accountsRes.value, status: 'ready' };
      emit(next);
      persist(next);
      return;
    }
    const code = graphqlErrorCode(accountsRes.reason);
    if (code === INTEGRATION_ERROR.unavailable) {
      emit({ uid, enabled, accounts: [], status: 'unavailable' });
      return;
    }
    warn('[useIntegrations] integrationAccounts failed:', accountsRes.reason);
    emit({ ...snapshot, enabled, status: 'error' });
  })().finally(() => {
    inFlight = null;
    inFlightUid = null;
  });
  return inFlight;
};

/**
 * `syncIntegrations` returns fresh accounts: they replace the cache outright
 * (contract §7a step 4), so the status line and the entry sublabel update
 * without another integrationAccounts query. Ignored if the account changed.
 */
export const applyIntegrationAccounts = (uid: string, accounts: IntegrationAccount[]): void => {
  if (snapshot.uid !== uid) {
    // The store hasn't loaded for this account (no surface mounted yet): just
    // refresh the cache its first load will seed from, and leave the flag to it.
    if (snapshot.uid === null) persist({ ...EMPTY, uid, accounts });
    return;
  }
  const next: IntegrationsSnapshot = { ...snapshot, accounts, status: 'ready' };
  emit(next);
  persist(next);
};

/** A per-account sync (Sync now) returns just that account: update it in place. */
export const mergeIntegrationAccounts = (uid: string, accounts: IntegrationAccount[]): void => {
  if (snapshot.uid !== uid || accounts.length === 0) return;
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const next: IntegrationsSnapshot = {
    ...snapshot,
    accounts: snapshot.accounts.map((a) => byId.get(a.id) ?? a),
  };
  emit(next);
  persist(next);
};

/** Test-only: reset the module store between cases. */
export const __resetIntegrationsStoreForTests = (): void => {
  snapshot = EMPTY;
  inFlight = null;
  inFlightUid = null;
  listeners.clear();
};

export interface UseIntegrations extends IntegrationsSnapshot {
  /**
   * Whether the Calendars & tasks entry points render. Hidden outside the
   * rollout cohort, EXCEPT when accounts already exist: a user must always be
   * able to reach Disconnect (contract C9).
   */
  visible: boolean;
  /** Re-reads the server; resolves with the accounts it now shows. */
  refresh: () => Promise<IntegrationAccount[]>;
  /** Resolves when the server confirms; the account then leaves `accounts`. */
  disconnect: (id: string, keepLists: boolean) => Promise<void>;
}

export const useIntegrations = (): UseIntegrations => {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!uid) {
      if (snapshot.uid !== null) emit(EMPTY);
      return;
    }
    if (!isIntegrationsApiConfigured()) return;
    if (snapshot.uid !== uid) void load(uid);
  }, [uid]);

  const refresh = useCallback(async (): Promise<IntegrationAccount[]> => {
    if (!uid || !isIntegrationsApiConfigured()) return [];
    await load(uid);
    return snapshot.uid === uid ? snapshot.accounts : [];
  }, [uid]);

  const disconnect = useCallback(async (id: string, keepLists: boolean) => {
    try {
      await disconnectIntegration(id, keepLists);
    } catch (err) {
      // NOT_FOUND: already gone (a second tap, another device). That IS the
      // outcome the user asked for, so fall through to the local removal.
      if (graphqlErrorCode(err) !== INTEGRATION_ERROR.notFound) throw err;
    }
    if (snapshot.uid !== uid) return;
    const next = { ...snapshot, accounts: snapshot.accounts.filter((a) => a.id !== id) };
    emit(next);
    persist(next);
  }, [uid]);

  const current = state.uid === uid ? state : EMPTY;
  return {
    ...current,
    visible: current.enabled || current.accounts.length > 0,
    refresh,
    disconnect,
  };
};

export default useIntegrations;
